import React, { useCallback, useEffect, useState } from "react";
import { Loader2, X, Download, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { adminPromoOrders, adminMarkCommissionPaid, adminMarkAllCommissionPaid, adminPromoCsv, formatINR, apiErrorMessage } from "../../api";
import { PromoStats } from "./PromoStats";
import { promoLink } from "./AdminPromos";

const STATUS_CLS = { pending: "bg-amber-100 text-amber-900", paid: "bg-emerald-100 text-emerald-900", void: "bg-[var(--sand)] text-[var(--espresso)]/60" };
const STATUS_LABEL = { pending: "Pending", paid: "Paid", void: "Cancelled" };
const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export const PromoOrdersPanel = ({ promo, range, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const params = { start: range.start || undefined, end: range.end || undefined };

  const load = useCallback(() => {
    adminPromoOrders(promo.id, { start: range.start || undefined, end: range.end || undefined })
      .then(setData).catch((e) => toast.error(apiErrorMessage(e)));
  }, [promo.id, range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged(); };
  const markPaid = async (orderId) => {
    try { await adminMarkCommissionPaid(promo.id, orderId); toast.success("Commission marked as paid"); refresh(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const markAll = async () => {
    try { const r = await adminMarkAllCommissionPaid(promo.id, params); toast.success(`${r.updated} order(s) marked as paid`); refresh(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const exportCsv = async () => {
    try {
      const blob = await adminPromoCsv(promo.id, params);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `promo-${promo.code}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error("Could not export CSV");
    }
  };

  const pendingCount = data?.orders.filter((o) => o.commission_status === "pending").length || 0;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="promo-orders-panel">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-7xl bg-[var(--cream)] h-full overflow-y-auto p-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[var(--copper)]">{promo.partner_name}{promo.partner_contact ? ` · ${promo.partner_contact}` : ""}</p>
            <h2 className="font-serif-display text-4xl mt-1">{promo.code}</h2>
            <p data-testid="promo-share-link" className="text-xs text-[var(--espresso)]/60 mt-2 select-all">{promoLink(promo.code)}</p>
          </div>
          <button onClick={onClose} data-testid="promo-orders-close" className="p-2 hover:text-[var(--copper)]"><X className="w-5 h-5" /></button>
        </div>
        {!data ? <Loader2 className="w-6 h-6 animate-spin text-[var(--copper)] mt-10" /> : (
          <>
            <PromoStats stats={data.stats} className="mt-6" />
            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={markAll} disabled={!pendingCount} data-testid="promo-mark-all-paid" className="h-10 px-5 bg-[var(--espresso)] text-[var(--cream)] text-[10px] tracking-[0.2em] uppercase flex items-center gap-2 disabled:opacity-40"><CheckCheck className="w-4 h-4" /> Mark all as paid ({pendingCount})</button>
              <button onClick={exportCsv} data-testid="promo-export-csv" className="h-10 px-5 border border-[var(--sand)] text-[10px] tracking-[0.2em] uppercase flex items-center gap-2 hover:border-[var(--copper)]"><Download className="w-4 h-4" /> Export CSV</button>
            </div>
            {data.orders.length === 0 ? (
              <p className="mt-10 text-[var(--espresso)]/60" data-testid="promo-orders-empty">No paid orders have used this code{range.start || range.end ? " in this date range" : ""} yet.</p>
            ) : (
              <div className="mt-6 bg-white border border-[var(--sand)] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] tracking-[0.14em] uppercase text-[var(--espresso)]/60 border-b border-[var(--sand)]">
                      {["Date", "Customer", "Products", "Amount", "Discount", "Commission", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.orders.map((o) => (
                      <tr key={o.order_id} data-testid={`promo-order-row-${o.order_id}`} className="border-b border-[var(--sand)] last:border-0 align-top">
                        <td className="px-4 py-3 whitespace-nowrap">{fmtDate(o.date)}<p className="text-[11px] text-[var(--espresso)]/50">#{o.order_id.slice(0, 8).toUpperCase()}</p></td>
                        <td className="px-4 py-3">{o.customer_name}<p className="text-[11px] text-[var(--espresso)]/50">{o.phone}<br />{o.email}</p></td>
                        <td className="px-4 py-3 max-w-[220px]">{o.products}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{formatINR(o.amount_after_discount)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{formatINR(o.discount)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{formatINR(o.commission)}</td>
                        <td className="px-4 py-3">
                          <span data-testid={`promo-order-status-${o.order_id}`} className={`inline-block px-2 py-1 text-[10px] tracking-widest uppercase ${STATUS_CLS[o.commission_status]}`}>{STATUS_LABEL[o.commission_status]}</span>
                          {o.commission_paid_at && <p className="text-[11px] text-[var(--espresso)]/50 mt-1">{fmtDate(o.commission_paid_at)}</p>}
                        </td>
                        <td className="px-4 py-3">
                          {o.commission_status === "pending" && (
                            <button onClick={() => markPaid(o.order_id)} data-testid={`promo-mark-paid-${o.order_id}`} className="h-8 px-3 border border-[var(--copper)] text-[var(--copper)] text-[10px] tracking-[0.14em] uppercase whitespace-nowrap hover:bg-[var(--copper)] hover:text-white transition-colors">Mark as paid</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
